import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { LayerType, MaterialIdsType, type ILayerProps, type IWatermarkLayerProps } from '@/version-types';

/** 水印 WASM 资源实例 ID。 */
export const AR_RES_ID = 'ar_res';
/** 水印 WASM 资源路径。 */
export const AR_RES_PATH = assetsConfig.text_ar_res;
/** 水印 WASM 公共参数资源。 */
export const AR_PUBLIC_PARAMS = assetsConfig.text_hbpArPublicParams;
/** 形状文字 WASM 资源实例 ID。 */
export const SHAPE_TEXT_RES_ID = 'textpatheffect';
/** 形状文字 WASM 资源路径。 */
export const SHAPE_TEXT_PATH = assetsConfig.text_effect_textpatheffect;

/** 水印图层历史记录使用的稳定类型名。 */
export const WATERMARK_HISTORY_KEY = 'change-watermark-action';

/** 单个文字水印允许包含的最大文本条数。 */
export const WATERMARK_TEXTS_MAX = 4;
/** 底层平铺单元固定使用的尺寸百分比，用户尺寸由渲染层单独缩放 */
export const WATERMARK_TILE_BASE_SIZE = 50;
/** 全屏水印默认尺寸百分比。 */
export const WATERMARK_DEFAULT_SIZE = 10;
/** 全屏水印默认间距百分比。 */
export const WATERMARK_DEFAULT_SPACING = 20;
/** 全屏水印默认交错百分比。 */
export const WATERMARK_DEFAULT_CROSS = 0;

/** 水印图层业务类型标识。 */
export const WATERMARK_LAYER_TYPE = 'watermark_layer_type';

/** 全屏水印支持调整的参数。 */
export enum FULL_SCREEN_TYPE {
    /** 水印尺寸。 */
    SIZE = 'size',
    /** 水印间距。 */
    SPACE = 'space',
    /** 交错排列。 */
    CROSS = 'crisscross',
    /** 水平偏移。 */
    OFFSET_X = 'offsetX',
    /** 垂直偏移。 */
    OFFSET_Y = 'offsetY',
    /** 旋转角度。 */
    ROTATE = 'evolve',
}

/** 水印素材类型。 */
export enum WATERMARK_TYPE {
    /** 图片水印。 */
    IMG = 'img',
    /** 文字水印。 */
    TEXT = 'text',
}

/** 新建水印图层时使用的内部基础数据模板。 */
const defaultWatermarkLayerData = {
    id: uuid(),
    layerType: LayerType.WATERMARK,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    rotate: 0,
    opacity: 1,
    title: '',
    materialIds: {},
    fitWidth: 0,
    fitHeight: 0,
    data: {
        /** 用户自定义上传的原始水印图片地址。 */
        originUrl: '',
        /** 底层生成并用于渲染的水印图片地址。 */
        url: '',
        /** 文字水印素材包地址。 */
        zipUrl: '',
        /** 是否以全屏平铺方式展示水印。 */
        isFullScreen: false,
        /** 全屏水印的横向偏移比例。 */
        offsetX: 0,
        /** 全屏水印的纵向偏移比例。 */
        offsetY: 0,
        /** 全屏水印的尺寸比例。 */
        size: 0,
        /** 全屏水印的间距比例。 */
        space: 0,
        /** 全屏水印的交错比例。 */
        cross: 0,
        /** 文字水印内容；图片水印为空数组。 */
        texts: [],
        /** 文字水印颜色；图片水印为空字符串。 */
        textColor: '',
        /** 文字水印字体；图片水印为空字符串。 */
        fontFamily: '',
        /** 颜色是否应用到文字素材的全部纹理。 */
        isColorApplyToAll: false,
    },
    lock: false,
    children: [],
    shine: true,
    hidden: false,
} as ILayerProps;

/** 基于内部模板创建拥有新 ID 的水印图层数据。 */
export const getDefaultWatermarkLayerData = (
    type: WATERMARK_TYPE,
    url?: string,
    textProps?: {
        /** 文字水印素材 ID。 */
        materialId: string;
        /** 文字水印素材包地址。 */
        zipUrl: string;
    },
) => {
    const watermarkLayerData = cloneDeep(defaultWatermarkLayerData);
    const data = watermarkLayerData.data as IWatermarkLayerProps;
    if (type === WATERMARK_TYPE.TEXT) {
        data.zipUrl = textProps?.zipUrl || '';
    } else {
        data.originUrl = url || '';
    }
    watermarkLayerData.id = uuid();
    watermarkLayerData.materialIds = watermarkLayerData.materialIds || {};
    if (textProps?.materialId) watermarkLayerData.materialIds[MaterialIdsType.WATERMARK] = [textProps.materialId];
    return watermarkLayerData;
};
